import { Navigate, Route, Routes } from "react-router-dom";
import React, { Suspense } from 'react';
import { AppRoute, IRouteRoleAccess, RouteRoleAccessList } from "./route-config";
import { BrowserRouter as Router } from 'react-router-dom';
import { useApplicationContext } from "src/context/auth-provider";
import { lazyWithDelay } from "src/utils/lazy-with-delay";
import LazyRouteLoaderComponent from "src/components/common/lazy-route-loader";
import { UserRole } from "src/data-access/models/enums";

const GuestLoginPageComponent = lazyWithDelay(() => import('../components/users/guest/login'));
const LoginPageComponent = lazyWithDelay(() => import('../components/auth/login'));
const SelfRegistrationPageComponent = lazyWithDelay(() => import('../components/auth/self-registration'));
const ChangePasswordPageComponent = lazyWithDelay(() => import('src/components/auth/change-password'));
const ForgotPasswordPageComponent = lazyWithDelay(() => import('src/components/auth/forgot-password'));
const ForbiddenAccessPageComponent = lazyWithDelay(() => import('src/components/auth/forbidden-access'));
const UnauthenticatedLayout = lazyWithDelay(() => import('../components/layouts/unauthenticated-layout'));
const AuthenticatedLayout = lazyWithDelay(() => import('src/components/layouts/authenticated-layout'));
const GuestAuthenticatedLayout = lazyWithDelay(() => import('src/components/layouts/guest-authenticated-layout'));
const GuestLogoutSuccessPageComponent = lazyWithDelay(() => import("src/components/users/guest/logout-success"));

const AppRouter: React.FC = () => {
    const { checkIsTokenValid, currentUser, guestUser, getRoleFromStore } = useApplicationContext();
    const isUserLoggedIn = checkIsTokenValid();
    const loggedInUserRole = getRoleFromStore();

    const configureCurrentUserProtectedRoute = (item: IRouteRoleAccess) => {
        if (!isUserLoggedIn)
            return <Navigate to={AppRoute.SignIn} />;

        if (!currentUser)
            return <></>;

        const canAccess =
            (item.canSolutionAdminAccesss && (currentUser.role === UserRole.SolutionUser && currentUser.isSolutionAdmin)) ||
            (item.canITBAdminAccesss && currentUser.role === UserRole.ITBAdmin) ||
            (item.canClientAdminAccess && currentUser.role === UserRole.ClientAdmin) ||
            (item.canUserAndLightUserAccess && currentUser.role === UserRole.SolutionUser);

        if (canAccess && !currentUser.hasAccessToAnySolution && item.route === AppRoute.CIAnalysisCreate)
            return <Navigate to={AppRoute.ForbiddenAccess} />;
        else if (!canAccess)
            return <Navigate to={AppRoute.ForbiddenAccess} />;

        return <>{item.renderComponent()}</>;
    };

    const configureGuestUserProtectedRoute = (item: IRouteRoleAccess) => {
        if (!isUserLoggedIn) return <Navigate to={AppRoute.SignIn} />;
        if (item.canTempUserAccess && guestUser) return <>{item.renderComponent()}</>;
        return <Navigate to={AppRoute.ForbiddenAccess} />;
    };

    return (
        <Router>
            <Suspense fallback={<LazyRouteLoaderComponent />}>
                <Routes>
                    {/* Routes for not-logged-in users */}
                    <Route element={<UnauthenticatedLayout />}>
                        <Route path={AppRoute.SignIn} element={<LoginPageComponent />} />
                        <Route path={AppRoute.ForgotPassword} element={<ForgotPasswordPageComponent />} />
                        <Route path={AppRoute.ChangePassword} element={<ChangePasswordPageComponent />} />
                        <Route path={AppRoute.SelfSignUp} element={<SelfRegistrationPageComponent />} />
                    </Route>

                    <Route path={AppRoute.GuestSignIn} element={<GuestLoginPageComponent />} />
                    <Route path={AppRoute.GuestLogoutSuccess} element={<GuestLogoutSuccessPageComponent />} />

                    {/* Routes for logged-in users */}
                    {
                        isUserLoggedIn &&
                        <>
                            {
                                <Route element={<AuthenticatedLayout />}>{
                                    RouteRoleAccessList
                                        .filter(x => !x.canTempUserAccess)
                                        .map((item: IRouteRoleAccess) => (
                                            <Route
                                                key={`auth_route_${item.route}`}
                                                path={item.route}
                                                element={configureCurrentUserProtectedRoute(item)}
                                            />
                                        ))
                                }
                                </Route>
                            }
                        </>
                    }

                    {
                        isUserLoggedIn && guestUser &&
                        <>
                            {
                                guestUser &&
                                <Route element={<GuestAuthenticatedLayout />}>{
                                    RouteRoleAccessList
                                        .filter(x => x.canTempUserAccess)
                                        .map((item: IRouteRoleAccess) => (
                                            <Route
                                                key={`auth_route_${item.route}`}
                                                path={item.route}
                                                element={configureGuestUserProtectedRoute(item)}
                                            />
                                        ))
                                }
                                </Route>
                            }
                        </>
                    }

                    {/* - Forbidden-access-page-route for users */}
                    <Route element={<UnauthenticatedLayout />}>
                        <Route path={AppRoute.ForbiddenAccess} element={<ForbiddenAccessPageComponent />} />
                    </Route>

                    {
                        !isUserLoggedIn &&
                        <Route index path="*" element={<Navigate to={AppRoute.SignIn} />} />
                    }

                    {
                        isUserLoggedIn && guestUser &&
                        <Route index path="*" element={<Navigate to={AppRoute.GuestHome} />} />
                    }

                    {
                        isUserLoggedIn && currentUser &&
                        <Route index path="*" element={<Navigate to={loggedInUserRole === UserRole.ITBAdmin ? AppRoute.ManageClients : AppRoute.ClientHome} />} />
                    }
                </Routes>
            </Suspense>
        </Router>
    );
}

export default AppRouter;
